import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const NAVY   = '#0D2B5E';
const GREEN  = '#6DC234';
const GREEN2 = '#5AAD28';

function FieldIcon({ children }) {
  return (
    <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94A3B8', display: 'flex', alignItems: 'center', pointerEvents: 'none' }}>
      {children}
    </span>
  );
}

const Register = ({ onSwitchToLogin, onSuccess }) => {
  const { register } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '', confirmPassword: '', firstName: '', lastName: '', companyName: '' });
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.email || !formData.password || !formData.companyName) { setError('Please fill in all required fields'); return; }
    if (formData.password !== formData.confirmPassword) { setError('Passwords do not match'); return; }
    if (formData.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    const result = await register(formData.email, formData.password, formData.firstName, formData.lastName, formData.companyName);
    setLoading(false);
    if (result.success) onSuccess();
    else setError(result.error || 'Registration failed. Please try again.');
  };

  const inp = (name) => ({
    width: '100%', boxSizing: 'border-box',
    padding: name === 'lastName' ? '12px 14px' : '12px 14px 12px 42px',
    borderRadius: '9px',
    border: `2px solid ${focused === name ? GREEN : '#E2E8F0'}`,
    fontSize: '14px', fontFamily: 'Inter, sans-serif',
    outline: 'none', color: NAVY, background: '#FAFAFA',
    transition: 'border-color 0.2s',
  });

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: '#F0F4FA', fontFamily: 'Inter, sans-serif' }}>
      {/* Left panel */}
      <div style={{
        display: 'none', flex: 1,
        background: `linear-gradient(150deg, ${NAVY} 0%, #071830 100%)`,
        padding: '48px', flexDirection: 'column', justifyContent: 'space-between',
      }} className="reg-left">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src="/logo.png" alt="JayChris EDI" style={{ width: '36px', height: '36px', objectFit: 'contain' }} />
          <span style={{ color: 'white', fontWeight: '700', fontSize: '16px' }}>JayChris EDI</span>
        </div>
        <div>
          <div style={{ color: GREEN, fontSize: '28px', fontWeight: '800', lineHeight: 1.2, marginBottom: '16px' }}>
            Start automating EDI compliance today.
          </div>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '15px', lineHeight: 1.6, marginBottom: '32px' }}>
            No contracts. No per-transaction fees. Setup in 15 minutes.
          </div>
          {['30-day free trial, no credit card required', 'Walmart, Target, Kohl\'s — pre-configured', '87% cheaper than SPS Commerce', '24/7 AI compliance support'].map(item => (
            <div key={item} style={{ display: 'flex', gap: '10px', marginBottom: '12px', alignItems: 'flex-start' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill={GREEN} style={{ flexShrink: 0, marginTop: '2px' }}><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
              <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '14px' }}>{item}</span>
            </div>
          ))}
        </div>
        <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: '12px' }}>© 2026 JayChris Management</div>
      </div>

      {/* Right panel — form */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', overflowY: 'auto' }}>
        <div style={{ width: '100%', maxWidth: '400px' }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '36px' }}>
            <img src="/logo.png" alt="JayChris EDI" style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
            <div>
              <div style={{ fontWeight: '800', fontSize: '15px', color: NAVY, lineHeight: 1.2 }}>JayChris EDI</div>
              <div style={{ fontSize: '11px', color: '#94A3B8', fontFamily: 'JetBrains Mono, monospace' }}>30-day free trial</div>
            </div>
          </div>

          <h1 style={{ fontSize: '24px', fontWeight: '800', color: NAVY, marginBottom: '6px', letterSpacing: '-0.5px' }}>Create your account</h1>
          <p style={{ fontSize: '14px', color: '#64748B', marginBottom: '28px' }}>
            Already have one?{' '}
            <button onClick={onSwitchToLogin} style={{ background: 'none', border: 'none', color: GREEN, fontWeight: '700', cursor: 'pointer', fontSize: '14px', fontFamily: 'Inter, sans-serif' }}>
              Sign in →
            </button>
          </p>

          {error && (
            <div style={{ padding: '12px 14px', borderRadius: '9px', background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', fontSize: '13px', marginBottom: '20px', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: '1px' }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Company */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: NAVY, marginBottom: '5px' }}>Company Name *</label>
              <div style={{ position: 'relative' }}>
                <FieldIcon><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg></FieldIcon>
                <input type="text" name="companyName" value={formData.companyName} onChange={e => setFormData({...formData, companyName: e.target.value})} onFocus={() => setFocused('companyName')} onBlur={() => setFocused(null)} placeholder="Acme Brand Inc." required style={inp('companyName')} />
              </div>
            </div>

            {/* First + Last */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: NAVY, marginBottom: '5px' }}>First Name</label>
                <div style={{ position: 'relative' }}>
                  <FieldIcon><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></FieldIcon>
                  <input type="text" name="firstName" value={formData.firstName} onChange={e => setFormData({...formData, firstName: e.target.value})} onFocus={() => setFocused('firstName')} onBlur={() => setFocused(null)} placeholder="Jay" style={inp('firstName')} />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: NAVY, marginBottom: '5px' }}>Last Name</label>
                <input type="text" name="lastName" value={formData.lastName} onChange={e => setFormData({...formData, lastName: e.target.value})} onFocus={() => setFocused('lastName')} onBlur={() => setFocused(null)} placeholder="Chris" style={inp('lastName')} />
              </div>
            </div>

            {/* Email */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: NAVY, marginBottom: '5px' }}>Email Address *</label>
              <div style={{ position: 'relative' }}>
                <FieldIcon><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg></FieldIcon>
                <input type="email" name="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} onFocus={() => setFocused('email')} onBlur={() => setFocused(null)} placeholder="you@company.com" required style={inp('email')} />
              </div>
            </div>

            {/* Password */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: NAVY, marginBottom: '5px' }}>Password *</label>
              <div style={{ position: 'relative' }}>
                <FieldIcon><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></FieldIcon>
                <input type="password" name="password" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} onFocus={() => setFocused('password')} onBlur={() => setFocused(null)} placeholder="Min. 8 characters" required style={inp('password')} />
              </div>
            </div>

            {/* Confirm */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: NAVY, marginBottom: '5px' }}>Confirm Password *</label>
              <div style={{ position: 'relative' }}>
                <FieldIcon><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></FieldIcon>
                <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={e => setFormData({...formData, confirmPassword: e.target.value})} onFocus={() => setFocused('confirmPassword')} onBlur={() => setFocused(null)} placeholder="••••••••" required style={inp('confirmPassword')} />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '14px',
                borderRadius: '9px', border: 'none',
                background: loading ? '#94A3B8' : `linear-gradient(135deg, ${GREEN}, ${GREEN2})`,
                color: 'white', fontSize: '15px', fontWeight: '700',
                fontFamily: 'Inter, sans-serif',
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: loading ? 'none' : `0 4px 14px rgba(109,194,52,0.4)`,
                transition: 'all 0.2s',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              }}
              onMouseEnter={e => { if (!loading) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 6px 20px rgba(109,194,52,0.5)`; } }}
              onMouseLeave={e => { if (!loading) { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = `0 4px 14px rgba(109,194,52,0.4)`; } }}
            >
              {loading ? (
                <><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg> Creating account…</>
              ) : 'Start Free Trial →'}
            </button>

            <p style={{ fontSize: '12px', color: '#94A3B8', textAlign: 'center', marginTop: '16px', lineHeight: 1.5 }}>
              No credit card required. By signing up you agree to our{' '}
              <a href="https://jaychrisedi.com/terms.html" style={{ color: GREEN }}>Terms</a> and{' '}
              <a href="https://jaychrisedi.com/privacy.html" style={{ color: GREEN }}>Privacy Policy</a>.
            </p>
          </form>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (min-width: 768px) { .reg-left { display: flex !important; } }
      `}</style>
    </div>
  );
};

export default Register;
